import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Search, Mail, KeyRound, Power, ShieldCheck, X, Check } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "" });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name, email}
  const [resetting, setResetting] = useState(null); // {id, email, new_password}

  const load = (search = "") => {
    setLoading(true);
    apiClient.get(`/admin/admins${search ? `?q=${encodeURIComponent(search)}` : ""}`)
      .then((r) => setAdmins(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const addAdmin = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await apiClient.post("/admin/admins", addForm);
      toast.success("Admin baru berhasil ditambahkan.");
      setShowAdd(false); setAddForm({ name: "", email: "", password: "" });
      load(q);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const saveEdit = async () => {
    try {
      await apiClient.patch(`/admin/admins/${editing.id}`, { name: editing.name, email: editing.email });
      toast.success("Data admin diperbarui.");
      setEditing(null); load(q);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const saveReset = async () => {
    try {
      await apiClient.post(`/admin/admins/${resetting.id}/password`, { new_password: resetting.new_password });
      toast.success("Password admin direset.");
      setResetting(null);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const toggleStatus = async (a) => {
    const next = a.status === "active" ? "suspended" : "active";
    try {
      await apiClient.patch(`/admin/admins/${a.id}/status`, { status: next });
      toast.success(next === "active" ? "Admin diaktifkan." : "Admin dinonaktifkan.");
      load(q);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <AdminLayout testid="admin-users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} className="text-primary" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">Kelola Admin</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="admin-add-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
          <UserPlus size={16} /> Tambah Admin
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Tambah admin baru, ubah email/nama, reset password, dan aktif/nonaktifkan akun admin.</p>

      <div className="relative max-w-sm mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Cari nama atau email..." data-testid="admin-search" className={`${inputCls} pl-9`} />
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-list">
          {admins.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold truncate">{a.name}</p>
                  {a.id === user?.id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-primary font-semibold">Anda</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${a.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {a.status === "active" ? "Aktif" : "Nonaktif"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{a.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing({ id: a.id, name: a.name, email: a.email })} data-testid={`admin-edit-${a.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><Mail size={13} /> Edit</button>
                <button onClick={() => setResetting({ id: a.id, email: a.email, new_password: "" })} data-testid={`admin-reset-${a.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><KeyRound size={13} /> Reset PW</button>
                <button onClick={() => toggleStatus(a)} disabled={a.id === user?.id} data-testid={`admin-status-${a.id}`} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${a.status === "active" ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}><Power size={13} /> {a.status === "active" ? "Nonaktifkan" : "Aktifkan"}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="admin-add-modal">
          <form onSubmit={addAdmin} className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Tambah Admin Baru</h3><button type="button" onClick={() => setShowAdd(false)} className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={16} /></button></div>
            <input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Nama lengkap" className={inputCls} data-testid="admin-add-name" />
            <input required type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-add-email" />
            <input required type="password" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} placeholder="Password awal (min. 8, huruf & angka)" className={inputCls} data-testid="admin-add-password" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Batal</button>
              <button type="submit" disabled={adding} data-testid="admin-add-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">{adding ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Simpan</button>
            </div>
          </form>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="admin-edit-modal">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Edit Admin</h3><button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={16} /></button></div>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nama" className={inputCls} data-testid="admin-edit-name" />
            <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-edit-email" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Batal</button>
              <button onClick={saveEdit} data-testid="admin-edit-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Check size={15} /> Simpan</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="admin-reset-modal">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Reset Password</h3><button onClick={() => setResetting(null)} className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={16} /></button></div>
            <p className="text-sm text-muted-foreground">Untuk akun: <span className="font-medium text-foreground">{resetting.email}</span></p>
            <input type="password" value={resetting.new_password} onChange={(e) => setResetting({ ...resetting, new_password: e.target.value })} placeholder="Password baru (min. 8, huruf & angka)" className={inputCls} data-testid="admin-reset-input" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setResetting(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Batal</button>
              <button onClick={saveReset} data-testid="admin-reset-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><KeyRound size={15} /> Reset</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
